import { postHourlyLocationPunch } from "../ApiHelper/url_Helpers";

/**
 * HourlyPunchService
 * ──────────────────
 * Rules:
 *  1. Fires exactly ONCE per elapsed hour after check-in time.
 *  2. Never fires concurrently (isPunching guard).
 *  3. Respects hourly_location_punch_enabled: if false, punch is stopped/disabled.
 *  4. Uses localStorage to persist the last-punch timestamp so page
 *     reloads / navigation don't reset or double-fire.
 *  5. Checks every 60 seconds; fires when (now - lastPunch) >= 1 hour.
 *  6. start() is idempotent — safe to call from multiple places.
 *  7. stop() on checkout clears state but does NOT remove the key
 *     (prevents double-fire on same-day re-checkin edge case;
 *     logout clears localStorage entirely).
 */

const ONE_HOUR_MS = 60 * 60 * 1000; // 3,600,000 ms
const CHECK_INTERVAL_MS = 60 * 1000; // poll every 60 s
const STORAGE_KEY = "hrms_last_hourly_punch_ts";
const ENABLED_STORAGE_KEY = "hrms_hourly_punch_enabled";

class HourlyPunchService {
  constructor() {
    this._intervalId = null;
    this._isRunning = false;
    this._isPunching = false; // prevents concurrent API calls
    this._checkInTime = null; // ISO string or Date from server
    this._enabled = null;

    if (typeof window !== "undefined") {
      window.addEventListener("dashboardProfileUpdated", (e) => {
        if (e?.detail && e.detail.hourly_location_punch_enabled !== undefined) {
          this.setEnabled(e.detail.hourly_location_punch_enabled);
        }
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Public: update permission state (hourly_location_punch_enabled)
  // ─────────────────────────────────────────────────────────────
  setEnabled(enabled) {
    this._enabled = Boolean(enabled);
    try {
      localStorage.setItem(ENABLED_STORAGE_KEY, this._enabled ? "true" : "false");
    } catch (_) {}

    console.log(
      `[HourlyPunch] hourly_location_punch_enabled set to: ${this._enabled}`
    );

    // If disabled while currently running, stop immediately
    if (!this._enabled && this._isRunning) {
      console.log("[HourlyPunch] Stopping hourly punch because hourly_location_punch_enabled is false.");
      this.stop();
    }
  }

  isEnabled() {
    if (this._enabled !== null && this._enabled !== undefined) {
      return this._enabled;
    }
    try {
      const stored = localStorage.getItem(ENABLED_STORAGE_KEY);
      if (stored !== null) {
        return stored === "true";
      }
    } catch (_) {}
    return true; // default true if not yet explicitly disabled
  }

  // ─────────────────────────────────────────────────────────────
  // Public: start after employee checks in
  // @param checkInTime  ISO string / Date returned by the server
  // @param isEnabled    Optional boolean override for hourly_location_punch_enabled
  // ─────────────────────────────────────────────────────────────
  start(checkInTime = null, isEnabled = null) {
    if (isEnabled !== null && isEnabled !== undefined) {
      this.setEnabled(isEnabled);
    }

    // If hourly_location_punch_enabled is false, do not start and ensure stopped
    if (!this.isEnabled()) {
      console.warn(
        "[HourlyPunch] hourly_location_punch_enabled is false – hourly punch In stopped."
      );
      this.stop();
      return;
    }

    if (this._isRunning) {
      // Already running – just refresh the reference check-in time
      this._checkInTime = checkInTime;
      console.log("[HourlyPunch] Already running – checkInTime updated.");
      return;
    }

    const token =
      sessionStorage.getItem("userToken") || localStorage.getItem("userToken");
    if (!token) {
      console.warn("[HourlyPunch] No auth token – service not started.");
      return;
    }

    this._isRunning = true;
    this._checkInTime = checkInTime;

    console.log(
      "[HourlyPunch] Service started. Check-in:",
      checkInTime || "unknown"
    );

    // Seed the reference timestamp from check-in time if no previous punch recorded
    this._seedReferenceIfNeeded();

    // Evaluate immediately (handles the case where an hour lapsed during page reload)
    this._checkAndPunch();

    // Poll every 60 s
    if (this._intervalId) clearInterval(this._intervalId);
    this._intervalId = setInterval(() => {
      this._checkAndPunch();
    }, CHECK_INTERVAL_MS);
  }

  // ─────────────────────────────────────────────────────────────
  // Public: stop when employee checks out or when disabled
  // ─────────────────────────────────────────────────────────────
  stop() {
    this._isRunning = false;
    this._checkInTime = null;
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    // Do NOT remove the storage key here — it prevents double-fire
    // on same-day re-checkin. Logout clears localStorage anyway.
    console.log("[HourlyPunch] Service stopped.");
  }

  // ─────────────────────────────────────────────────────────────
  // Internal: seed localStorage reference on first start
  // ─────────────────────────────────────────────────────────────
  _seedReferenceIfNeeded() {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return; // already seeded from a previous punch

    // Use check-in time as the base reference so the first punch fires
    // exactly 1 hour after check-in, not 1 hour after page load.
    if (this._checkInTime) {
      const checkInMs = new Date(this._checkInTime).getTime();
      if (!isNaN(checkInMs)) {
        localStorage.setItem(STORAGE_KEY, checkInMs.toString());
        console.log(
          "[HourlyPunch] Reference seeded from check-in:",
          new Date(checkInMs).toLocaleTimeString()
        );
        return;
      }
    }

    // Fallback: use current time as reference
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());
    console.log(
      "[HourlyPunch] Reference seeded from now:",
      new Date(now).toLocaleTimeString()
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Internal: called every 60 s – fire punch only if 1 h elapsed
  // ─────────────────────────────────────────────────────────────
  _checkAndPunch() {
    if (!this._isRunning) return;

    if (!this.isEnabled()) {
      console.warn(
        "[HourlyPunch] hourly_location_punch_enabled is false – stopping hourly punch."
      );
      this.stop();
      return;
    }

    if (this._isPunching) {
      console.log("[HourlyPunch] Punch already in progress – skipping.");
      return;
    }

    const lastPunchStr = localStorage.getItem(STORAGE_KEY);
    const lastPunchMs = lastPunchStr ? parseInt(lastPunchStr, 10) : null;

    if (!lastPunchMs) {
      // Seed missing (shouldn't happen) and wait another cycle
      this._seedReferenceIfNeeded();
      return;
    }

    const now = Date.now();
    const elapsed = now - lastPunchMs;

    console.log(
      `[HourlyPunch] Elapsed since last punch: ${Math.floor(elapsed / 60000)} min`
    );

    if (elapsed >= ONE_HOUR_MS) {
      this._triggerPunch();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Internal: single punch execution – fully guarded
  // ─────────────────────────────────────────────────────────────
  async _triggerPunch() {
    if (this._isPunching) return; // double-call guard
    this._isPunching = true;

    console.log("[HourlyPunch] Firing hourly punch…");

    try {
      const coords = await this._getCoords();
      const locationName = await this._reverseGeocode(
        coords.latitude,
        coords.longitude
      );

      const payload = {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        location_name: locationName || "Auto Hourly Location",
      };

      await postHourlyLocationPunch(payload);

      const now = Date.now();
      // Advance reference by exactly ONE_HOUR_MS from the previous reference
      // (not from `now`) so drift doesn't accumulate.
      const lastPunchStr = localStorage.getItem(STORAGE_KEY);
      const lastPunchMs = lastPunchStr ? parseInt(lastPunchStr, 10) : now;
      const nextReference = lastPunchMs + ONE_HOUR_MS;

      // If we're more than one full interval behind (e.g. device was sleeping),
      // just move the reference forward by 1 h from now to avoid instant re-fire.
      const finalReference =
        now - nextReference > ONE_HOUR_MS ? now : nextReference;

      localStorage.setItem(STORAGE_KEY, finalReference.toString());

      window.dispatchEvent(
        new CustomEvent("hourlyPunchSuccess", {
          detail: { timestamp: now, payload },
        })
      );

      console.log(
        "[HourlyPunch] ✓ Punch success at:",
        new Date(now).toLocaleTimeString(),
        "| Next due ~",
        new Date(finalReference + ONE_HOUR_MS).toLocaleTimeString()
      );
    } catch (err) {
      console.warn("[HourlyPunch] Punch failed:", err?.message || err);
      // Do NOT advance the reference – retry on the next minute cycle
    } finally {
      this._isPunching = false;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────
  _getCoords() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(new Error("Geolocation not supported"));
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: Number(pos.coords.latitude.toFixed(6)),
            longitude: Number(pos.coords.longitude.toFixed(6)),
            accuracy: Math.round(pos.coords.accuracy || 10),
          }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    });
  }

  async _reverseGeocode(lat, lon) {
    try {
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
      );
      if (res.ok) {
        const data = await res.json();
        const parts = [
          data.locality || data.city,
          data.principalSubdivision,
          data.countryName,
        ].filter(Boolean);
        if (parts.length) return parts.join(", ");
      }
    } catch (_) {}

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`,
        { headers: { "Accept-Language": "en" } }
      );
      if (res.ok) {
        const data = await res.json();
        const addr = data.address || {};
        const parts = [
          addr.suburb || addr.neighbourhood || addr.residential,
          addr.city || addr.town || addr.village,
          addr.state,
        ].filter(Boolean);
        if (parts.length) return parts.join(", ");
      }
    } catch (e) {
      console.warn("[HourlyPunch] Reverse geocode failed:", e);
    }

    return "Current Location";
  }
}

const hourlyPunchService = new HourlyPunchService();
export default hourlyPunchService;
